import React from 'react';
import { Box, Typography, Link as MuiLink, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { WAITLIST_URL } from '../../constant/constant';

interface WaitlistMessageProps {
  /** When `true` the banner is rendered; otherwise nothing is shown. */
  visible: boolean;
}

/**
 * Displays a waitlist call-to-action when one or more membership plans are
 * sold out / archived.  Uses the centralized WAITLIST_URL constant so the
 * link only needs to be changed in one place.
 */
const WaitlistMessage: React.FC<WaitlistMessageProps> = ({ visible }) => {
  const theme = useTheme();

  if (!visible) return null;

  return (
    <Box
      sx={{
        textAlign: 'center',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 2.5 },
        mt: { xs: 2, sm: 3 },
        mb: { xs: 2, sm: 3 },
        mx: 'auto',
        maxWidth: '800px',
        backgroundColor: '#FFF8E1',
        border: '1px solid #FFE082',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
      data-testid="waitlist-message"
    >
      <InfoOutlinedIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />

      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 500,
          fontSize: { xs: '0.875rem', sm: '1rem' },
        }}
      >
        Is your desired membership plan sold out? Join our waitlist here:
      </Typography>

      <MuiLink
        href={WAITLIST_URL}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '0.875rem', sm: '1rem' },
          color: theme.palette.primary.main,
        }}
        data-testid="waitlist-link"
      >
        mindright.io/join
      </MuiLink>
    </Box>
  );
};

export default React.memo(WaitlistMessage);
